import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast.service';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { EToolListType, EToolStatus } from 'src/app/enum/ai-tool.enum';
import { IAiTool, IAiToolSuggestion, IAiToolUseCase } from 'src/app/models/ai-tool.model';

import { ISortOption } from 'src/app/models/sort.model';
import { AiToolService } from 'src/app/services/aiTool.service';
import { AiToolUseCaseService } from 'src/app/services/aiToolUseCases.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-ai-tool-list',
  templateUrl: './ai-tool-list.component.html',
  styleUrls: ['./ai-tool-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiToolListComponent {
  @Input() listType: EToolListType;

  private subscription = new Subscription();
  public filterOptions: ISortOption = {
    sortBy: 'createdAt',
    sortOrder: 'desc',
    searchParam: '',
    filterBy: EToolStatus.TESTED,
  };

  public selectedTool: IAiTool;
  public selectedUseCase: IAiToolUseCase;
  public showAiToolEditDialog = false;

  public confirmDialog = false;

  public toolReviews: IAiTool[] = [];
  public toolSuggestions: IAiToolSuggestion[] = [];
  public useCases: IAiToolUseCase[] = [];
  public EToolStatus = EToolStatus;
  public EToolListType = EToolListType;
  public storageKey = 'toolReviewFilters';
  private searchSubject = new Subject<void>();
  public searchHovered = false;

  public currentPage: number = 1;
  public totalCount: number = 1;

  public itemsPerPage: number = 3;

  constructor(
    private aiToolService: AiToolService,
    private aiToolUseCaseService: AiToolUseCaseService,
    private toastService: ToastService,
    public router: Router,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.storageKey = this.listType === EToolListType.SUGGESTION ? 'toolSuggestionFilters' : 'toolReviewFilters';
    this.filterOptions.filterBy = this.listType === EToolListType.SUGGESTION ? EToolStatus.ALL : EToolStatus.TESTED;
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(700)).subscribe(() => {
        this.searchTools();
      }),
    );
    this.getFiltersFromLocalStorage();
    this.getAllTools();
  }

  public onSearch() {
    this.searchSubject.next();
  }

  public onOpenAiToolDialog(tool: IAiTool): void {
    if (tool) {
      this.selectedTool = tool;
    } else {
      this.selectedTool = null;
    }

    this.showAiToolEditDialog = true;
  }

  public onOpenUseCaseDialog(useCase: IAiToolUseCase): void {
    if (useCase) {
      this.selectedUseCase = useCase;
    } else {
      this.selectedUseCase = null;
    }
    this.showAiToolEditDialog = true;
  }

  public updateToolReview(tool: IAiTool): void {
    this.subscription.add(
      this.aiToolService.updateToolReview(tool).subscribe({
        next: () => {
          this.getAllTools();
          this.showAiToolEditDialog = false;
          this.toastService.success('Tool was successfully updated');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }
  public updateToolUseCase(useCase: IAiToolUseCase): void {
    this.subscription.add(
      this.aiToolUseCaseService.updateToolUseCase(useCase).subscribe({
        next: () => {
          this.getAllUseCases();
          this.showAiToolEditDialog = false;
          this.toastService.success('Use case was successfully updated');
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public searchTools(): void {
    localStorage.setItem(this.storageKey, JSON.stringify(this.filterOptions));

    if (this.listType === EToolListType.REVIEW) {
      this.getAllTools();
    } else if (this.listType === EToolListType.USECASE) {
      this.getAllUseCases();
    } else if (this.listType === EToolListType.SUGGESTION) {
      this.getAllToolSuggestions();
    }
  }

  private getFiltersFromLocalStorage(): void {
    const filters = localStorage.getItem(this.storageKey);
    if (filters) {
      this.filterOptions = JSON.parse(filters);
    }
  }

  public resetSearch(): void {
    this.filterOptions.searchParam = '';
    localStorage.setItem(this.storageKey, JSON.stringify(this.filterOptions));
    this.getAllTools();
  }

  private getAllTools(): void {
    if (this.listType === EToolListType.SUGGESTION) {
      this.getAllToolSuggestions();
    } else if (this.listType === EToolListType.REVIEW) {
      this.getAllToolReviews();
    } else {
      this.getAllUseCases();
    }
  }

  public onPageChange(page: number): void {
    this.currentPage = page;

    if (this.listType === EToolListType.REVIEW) {
      this.getAllTools();
    } else {
      this.getAllUseCases();
    }
  }

  public sortOptionSelected(sortOption: ISortOption): void {
    this.filterOptions.sortBy = sortOption.sortBy;
    this.filterOptions.sortOrder = sortOption.sortOrder;

    localStorage.setItem(this.storageKey, JSON.stringify(this.filterOptions));
    this.currentPage = 1;
    this.getAllTools();
  }

  public filterOptionSelected(filterOptions: ISortOption): void {
    this.filterOptions.filterBy = filterOptions.filterBy as EToolStatus;

    localStorage.setItem(this.storageKey, JSON.stringify(this.filterOptions));

    this.getAllToolSuggestions();
  }

  public getAllToolReviews(): void {
    this.filterOptions.currentPage = this.currentPage;
    this.subscription.add(
      this.aiToolService.getAllToolReviews(this.filterOptions, this.currentPage, this.itemsPerPage).subscribe({
        next: (res: any): void => {
          this.toolReviews = res.DATA.data; // Update the tools list
          this.totalCount = res.DATA.totalCount; // Store total count for pagination
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public getAllUseCases(): void {
    this.subscription.add(
      this.aiToolUseCaseService.getAllUseCases(this.filterOptions).subscribe({
        next: (res: any): void => {
          this.useCases = res.DATA.data; // Update the tools list
          this.totalCount = res.DATA.totalCount; // Store total count for pagination
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public getAllToolSuggestions(): void {
    this.subscription.add(
      this.aiToolService.getAllToolSuggestions(this.filterOptions.filterBy, this.filterOptions.searchParam).subscribe({
        next: (res: any): void => {
          this.toolSuggestions = res.DATA;
        },
        error: () => {
          this.toastService.error('Something went wrong');
        },
      }),
    );
  }

  public toggleClosedTools(): void {
    if (this.filterOptions.filterBy === EToolStatus.CLOSED) {
      this.filterOptions.filterBy = EToolStatus.TESTED;
    } else {
      this.filterOptions.filterBy = EToolStatus.CLOSED;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(this.filterOptions));
    this.getAllTools();
  }

  public onToolSuggestionRemoved(tool: IAiToolSuggestion): void {
    this.toolSuggestions = this.toolSuggestions.filter((f) => f._id !== tool._id);
  }
  public onToolReviewRemoved(tool: IAiTool): void {
    this.toolReviews = this.toolReviews.filter((f) => f._id !== tool._id);
  }
  public onToolUseCaseRemoved(useCase: IAiToolUseCase): void {
    this.useCases = this.useCases.filter((f) => f._id !== useCase._id);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
